import React, { useState, useEffect } from 'react';

import Alert from './ui/Alert';
import Button from './ui/Button';
import Card from './ui/Card';
import EmptyState from './ui/EmptyState';
import ProductCard from './ui/ProductCard';
import SectionHeader from './ui/SectionHeader';
import Spinner from './ui/Spinner';
import TextField from './ui/TextField';

const DETAILS_INDEX_KEY = 'premtech.detailsIndex.v1';
const DETAILS_INDEX_META_KEY = 'premtech.detailsIndex.meta.v1';

function safeParseJson(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function ProductOverview() {
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [indexProgress, setIndexProgress] = useState({ done: 0, total: 0 });
  const [detailsIndex, setDetailsIndex] = useState(() => {
    const raw = localStorage.getItem(DETAILS_INDEX_KEY);
    const parsed = safeParseJson(raw || '{}', {});
    return parsed && typeof parsed === 'object' ? parsed : {};
  });

  useEffect(() => {
    let isMounted = true;
    setError('');
    setLoading(true);

    fetch('/api/products')
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`API error ${res.status}: ${body || res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setAllProducts(list);
        setProducts(list);
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) return;
        setError(e?.message || 'Failed to fetch');
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const indexComplete =
    allProducts.length > 0 && Object.keys(detailsIndex || {}).length >= allProducts.length;

  const startIndexing = async () => {
    if (!allProducts.length) return;
    setError('');
    setIndexing(true);

    let cursor = 0;
    const total = allProducts.length;
    let nextCursor = 0;

    const merged = { ...(detailsIndex || {}) };
    setIndexProgress({ done: Object.keys(merged).length, total });

    try {
      while (nextCursor !== null && nextCursor < total) {
        cursor = nextCursor;
        const res = await fetch(
          `/api/products?warm=1&cursor=${encodeURIComponent(cursor)}&limit=20&returnDetails=1`
        );
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`API error ${res.status}: ${body || res.statusText}`);
        }
        const data = await res.json();

        const items = Array.isArray(data?.items) ? data.items : [];
        for (const it of items) {
          if (!it?.id) continue;
          merged[it.id] = {
            praktischeInfoText: String(it.praktischeInfoText || ''),
            eigenschappenText: String(it.eigenschappenText || ''),
            documenten: Array.isArray(it.documenten) ? it.documenten : []
          };
        }

        localStorage.setItem(DETAILS_INDEX_KEY, JSON.stringify(merged));
        localStorage.setItem(
          DETAILS_INDEX_META_KEY,
          JSON.stringify({ updatedAt: Date.now(), total })
        );

        setDetailsIndex({ ...merged });
        setIndexProgress({ done: Object.keys(merged).length, total });

        nextCursor = typeof data?.nextCursor === 'number' ? data.nextCursor : null;
      }
    } catch (e) {
      setError(e?.message || 'Indexeren mislukt');
    } finally {
      setIndexing(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const q = search.trim();

    if (!q) {
      setProducts(allProducts);
      setSearching(false);
      return () => {
        isMounted = false;
      };
    }

    if (indexComplete) {
      const needle = q.toLowerCase();
      const filtered = allProducts.filter((p) => {
        const d = detailsIndex?.[p?.id] || null;
        const blob = [
          p?.name,
          p?.omschrijvingText,
          p?.description,
          p?.technischeInfoText,
          p?.tech,
          d?.praktischeInfoText,
          d?.eigenschappenText,
          (d?.documenten || []).map((x) => x?.title || x?.href).join('\n')
        ]
          .map((v) => String(v || ''))
          .join('\n')
          .toLowerCase();

        return blob.includes(needle);
      });

      setProducts(filtered);
      setSearching(false);
      return () => {
        isMounted = false;
      };
    }

    const handle = setTimeout(() => {
      setSearching(true);
      fetch(`/api/products?q=${encodeURIComponent(q)}`)
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(`API error ${res.status}: ${body || res.statusText}`);
          }
          return res.json();
        })
        .then((data) => {
          if (!isMounted) return;
          setProducts(Array.isArray(data) ? data : []);
          setSearching(false);
        })
        .catch((e) => {
          if (!isMounted) return;
          setError(e?.message || 'Failed to fetch');
          setSearching(false);
        });
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [search, allProducts]);

  return (
    <div>
      <SectionHeader
        title="Producten"
        subtitle="Alle PremTech producten binnen de 7 hoofdcategorieën."
        right={
          <div style={{ width: 'min(420px, 100%)' }}>
            <TextField
              label="Zoeken"
              placeholder="Zoek op naam, omschrijving, technische info, praktische info of eigenschappen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              name="search"
              id="search"
            />

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant={indexComplete ? 'ghost' : 'secondary'}
                size="sm"
                onClick={startIndexing}
                disabled={indexing || loading || !allProducts.length}
              >
                {indexComplete
                  ? 'Index is compleet'
                  : indexing
                    ? 'Indexeren…'
                    : 'Indexeer alle productinfo'}
              </Button>
              <span className="muted" style={{ fontSize: 12 }}>
                {indexProgress.total ? `${indexProgress.done}/${indexProgress.total}` : null}
              </span>
            </div>
          </div>
        }
      />

      {error ? (
        <Alert tone="danger" title="Kon producten niet laden" style={{ marginBottom: 18 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Card className="pageCard" style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner label="Producten laden…" />
        </Card>
      ) : products.length === 0 ? (
        <EmptyState
          title="Geen resultaten"
          description="Pas je zoekterm aan of probeer een andere spelling."
        />
      ) : (
        <div>
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            {searching ? 'Zoeken…' : `${products.length} producten`}
          </div>
          <div className="productsGrid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductOverview;
